import { useCallback, useEffect, useRef, useState } from "react";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { DrawingViewerTopBar } from "../../../components/dashboard/drawings/drawing-viewer-top-bar";
import { DrawingViewerToolRail } from "../../../components/dashboard/drawings/drawing-viewer-tool-rail";
import { DrawingViewerCanvas } from "../../../components/dashboard/drawings/drawing-viewer-canvas";
import { DrawingViewerPanel } from "../../../components/dashboard/drawings/drawing-viewer-panel";
import { DrawingModuleConfigDialog } from "../../../components/dashboard/drawings/drawing-module-config-dialog";
import { DrawingCalibrationDialog } from "../../../components/dashboard/drawings/drawing-calibration-dialog";
import { DrawingAddObservationDialog } from "../../../components/dashboard/drawings/drawing-add-observation-dialog";
import { DrawingObservationSketchOverlay } from "../../../components/dashboard/drawings/drawing-observation-sketch-overlay";
import { DrawingModuleDetailDialog } from "../../../components/dashboard/drawings/drawing-module-detail-dialog";
import { DrawingChecklistFillDialog } from "../../../components/dashboard/drawings/drawing-checklist-fill-dialog";
import { DrawingIfcAlignmentDialog } from "../../../components/dashboard/drawings/drawing-ifc-alignment-dialog";
import { useDrawingViewer } from "../../../components/dashboard/drawings/useDrawingViewer";
import { IMMERSIVE_VIEWER_SHELL_CLASS } from "../../../components/dashboard/immersive-viewer-layout";
import {
  getDocumentPlacementSession,
  registerDrawingViewerPlacementBridge,
  subscribeDocumentPlacement,
  updateDocumentPlacementSession,
} from "../../../components/dashboard/drawings/drawingDocumentPlacementSession";
import {
  DrawingViewer3dFab,
  DrawingViewerMobileBottomBar,
  DrawingViewerMobileDrawingPicker,
  DrawingViewerMobileModulesSheet,
  DrawingViewerMobileMoreSheet,
  DrawingViewerMobileToolsSheet,
  DrawingViewerMobileTopBanner,
  canShowDrawing3dFab,
  useIsDrawingMobile,
} from "../../../components/dashboard/drawings/drawing-viewer-mobile";
import {
  checklistIdFromModule,
  documentIdFromModule,
  findModuleByDocumentId,
  observationKindFromModule,
  parseModuleMeta,
} from "../../../components/dashboard/drawings/drawingObservationUtils";
import {
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

export const DrawingViewer = () => {
  const viewer = useDrawingViewer();
  const isMobile = useIsDrawingMobile();
  const modulesScrollTopRef = useRef(0);
  const viewerShellRef = useRef(null);
  const [mobileSheet, setMobileSheet] = useState(null);
  /** Checklist id kept across pin-focus soft-close so fill dialog can resume without refetch. */
  const [fillChecklistId, setFillChecklistId] = useState(null);
  /** Visibility of fill dialog — false while pin-focus / document placement shows the canvas. */
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  /** How to resume fill dialog: cache (pin focus) vs soft network refresh (after place pin). */
  const [fillResumeMode, setFillResumeMode] = useState("network");
  const [focusedDocumentId, setFocusedDocumentId] = useState(null);
  /** When set, fill/detail dialogs are closed so the pin is visible; reopen CTA is shown. */
  const [pinFocusChecklistId, setPinFocusChecklistId] = useState(null);
  /** Soft-closed deviation/module detail during pin-focus (reopen CTA). */
  const [pinFocusDetailModule, setPinFocusDetailModule] = useState(null);
  const suspendedFillChecklistIdRef = useRef(null);
  /** Soft-closed deviation/module detail while placing a document pin. */
  const suspendedModuleDetailRef = useRef(null);
  const fillSessionRef = useRef({
    checklistId: null,
    scrollTop: 0,
    focusedDocumentId: null,
    open: false,
  });

  const clearPinFocusMode = useCallback(() => {
    setPinFocusChecklistId(null);
    setPinFocusDetailModule(null);
  }, []);

  const softCloseFillDialog = useCallback(
    (overrideChecklistId = null) => {
      const id = overrideChecklistId ?? fillChecklistId;
      fillSessionRef.current = {
        ...fillSessionRef.current,
        checklistId: id,
        focusedDocumentId,
        open: false,
      };
      setFillDialogOpen(false);
    },
    [fillChecklistId, focusedDocumentId]
  );

  const hardCloseFillDialog = useCallback(() => {
    fillSessionRef.current = {
      checklistId: null,
      scrollTop: 0,
      focusedDocumentId: null,
      open: false,
    };
    setFillChecklistId(null);
    setFillDialogOpen(false);
    setFillResumeMode("network");
    setFocusedDocumentId(null);
  }, []);

  const openFillChecklist = useCallback(
    (checklistId, { resumeMode = "network" } = {}) => {
      if (checklistId == null) return;
      clearPinFocusMode();
      setFillResumeMode(resumeMode);
      setFillChecklistId(checklistId);
      setFillDialogOpen(true);
      setMobileSheet(null);
      fillSessionRef.current = {
        ...fillSessionRef.current,
        checklistId,
        open: true,
      };
    },
    [clearPinFocusMode]
  );

  const enterPinFocusMode = useCallback(
    (module, { documentId = null, checklistId = null } = {}) => {
      const resolvedChecklistId =
        checklistId ??
        checklistIdFromModule(module) ??
        fillChecklistId ??
        fillSessionRef.current.checklistId ??
        null;
      if (resolvedChecklistId == null && !module) return;

      const kind = module ? observationKindFromModule(module) : null;
      // Reopen CTA is only for photo/document pins with checklist context —
      // never for module_type checklist pins.
      const showChecklistReopenCta =
        kind !== "checklist" && resolvedChecklistId != null;

      const docId = documentId ?? documentIdFromModule(module) ?? null;
      if (docId != null) setFocusedDocumentId(docId);

      // Select/highlight only — do not pan/zoom/center the drawing on pin focus.
      if (module) {
        viewer.setSelectedModule(module);
      }

      // Soft-close fill dialog (keep checklist id + cache) so pin is visible.
      if (resolvedChecklistId != null && fillChecklistId == null) {
        setFillChecklistId(resolvedChecklistId);
      }
      if (fillChecklistId != null || fillDialogOpen || resolvedChecklistId != null) {
        softCloseFillDialog(resolvedChecklistId);
        setFillResumeMode("cache");
      }

      // Soft-close avvik/modul-detalj so the highlighted pin is visible on canvas.
      const openDetail = viewer.moduleDetailModule;
      if (openDetail) {
        suspendedModuleDetailRef.current = openDetail;
        setPinFocusDetailModule(openDetail);
      } else {
        setPinFocusDetailModule(null);
      }
      viewer.setModuleDetailModule(null);
      setMobileSheet(null);

      if (showChecklistReopenCta) {
        setPinFocusChecklistId(resolvedChecklistId);
      } else {
        setPinFocusChecklistId(null);
      }
    },
    [viewer, fillChecklistId, fillDialogOpen, softCloseFillDialog]
  );

  const handleReopenDetailFromPinFocus = useCallback(() => {
    const detail =
      pinFocusDetailModule || suspendedModuleDetailRef.current || null;
    clearPinFocusMode();
    if (detail) {
      viewer.setModuleDetailModule(detail);
    }
  }, [pinFocusDetailModule, clearPinFocusMode, viewer]);

  const handleFocusDocumentPin = useCallback(
    (doc, { pinFocus = false, center = false } = {}) => {
      if (!doc?.id) return;
      setFocusedDocumentId(doc.id);
      fillSessionRef.current = {
        ...fillSessionRef.current,
        focusedDocumentId: doc.id,
      };
      const module = findModuleByDocumentId(viewer.modules, doc.id);
      if (!module) return;
      // Switch page if needed so the pin is visible — never pan/center the canvas.
      const meta = parseModuleMeta(module);
      if (
        typeof meta.page === "number" &&
        typeof viewer.handlePageChange === "function" &&
        meta.page !== viewer.page
      ) {
        viewer.handlePageChange(meta.page);
      }
      viewer.setSelectedModule(module);
      // `center` is a legacy alias for pinFocus (never pans the canvas).
      const enterFocus = pinFocus || center;
      if (enterFocus) {
        // Click → soft-close fill/detail dialog, highlight pin, show reopen CTA.
        enterPinFocusMode(module, { documentId: doc.id });
      }
    },
    [viewer, enterPinFocusMode]
  );

  const handleFocusModuleOnDrawing = useCallback(
    (module) => {
      if (!module) return;
      enterPinFocusMode(module);
    },
    [enterPinFocusMode]
  );

  const handleReopenChecklistFromPinFocus = useCallback(() => {
    const checklistId =
      pinFocusChecklistId ??
      fillChecklistId ??
      fillSessionRef.current.checklistId;
    if (checklistId == null) return;
    clearPinFocusMode();
    openFillChecklist(checklistId, { resumeMode: "cache" });
  }, [
    pinFocusChecklistId,
    fillChecklistId,
    clearPinFocusMode,
    openFillChecklist,
  ]);

  const handleFillSessionSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    fillSessionRef.current = {
      ...fillSessionRef.current,
      ...snapshot,
    };
  }, []);

  /** From a deviation-linked photo pin: open the in-context avvik dialog. */
  const handleOpenDeviationFromPhoto = useCallback(
    (deviationId, photoModule = null) => {
      if (deviationId == null) return;
      clearPinFocusMode();

      const linked =
        (viewer.modules || []).find(
          (mod) =>
            String(mod.deviation_id) === String(deviationId) &&
            observationKindFromModule(mod) === "deviation"
        ) || null;

      if (linked) {
        viewer.setModuleDetailModule(linked);
        return;
      }

      const nested =
        photoModule?.deviation ||
        (photoModule && String(photoModule.deviation_id) === String(deviationId)
          ? { id: deviationId }
          : { id: deviationId });
      const meta = photoModule ? parseModuleMeta(photoModule) : {};
      viewer.setModuleDetailModule({
        id: `deviation-preview-${deviationId}`,
        deviation_id: deviationId,
        deviation: {
          id: deviationId,
          number: nested.number,
          displayTitle: nested.displayTitle || nested.title || meta.deviation_title,
          title: nested.title || meta.deviation_title,
          status: nested.status,
        },
        module_type: "circle",
        color: "#e5484d",
        path_data: JSON.stringify({
          kind: "deviation",
          title:
            nested.displayTitle ||
            nested.title ||
            meta.deviation_title ||
            null,
        }),
        _previewOnly: true,
      });
    },
    [viewer, clearPinFocusMode]
  );

  // Scoped page-zoom lock while the drawing viewer shell is mounted (no global viewport meta).
  useEffect(() => {
    if (viewer.loading || !viewer.drawing) return undefined;

    const root = viewerShellRef.current;
    if (!root) return undefined;

    const blockMultiTouchZoom = (event) => {
      if (event.touches && event.touches.length >= 2 && event.cancelable) {
        event.preventDefault();
      }
    };
    const blockGesture = (event) => {
      if (event.cancelable) event.preventDefault();
    };

    root.addEventListener("touchmove", blockMultiTouchZoom, { passive: false });
    root.addEventListener("gesturestart", blockGesture, { passive: false });
    root.addEventListener("gesturechange", blockGesture, { passive: false });

    return () => {
      root.removeEventListener("touchmove", blockMultiTouchZoom);
      root.removeEventListener("gesturestart", blockGesture);
      root.removeEventListener("gesturechange", blockGesture);
    };
  }, [viewer.loading, viewer.drawing?.id]);

  const closeMobileSheet = useCallback(() => setMobileSheet(null), []);

  const openMobileSheet = useCallback((sheet) => {
    setMobileSheet((current) => (current === sheet ? null : sheet));
  }, []);

  // Let checklist uploads place pins on this open canvas (suspend fill dialog first).
  useEffect(() => {
    if (!viewer.drawing?.id) return undefined;

    const startPlacement = (session) => {
      if (!session || String(session.drawingId) !== String(viewer.drawing.id)) {
        return;
      }
      const activeFillId =
        fillChecklistId ||
        session.suspendFillChecklistId ||
        fillSessionRef.current.checklistId;
      if (activeFillId) {
        suspendedFillChecklistIdRef.current = activeFillId;
        if (fillChecklistId == null) {
          setFillChecklistId(activeFillId);
        }
        updateDocumentPlacementSession({
          suspendFillChecklistId: activeFillId,
        });
        softCloseFillDialog();
        setFillResumeMode("cache-soft-refresh");
      } else if (session.suspendFillChecklistId) {
        suspendedFillChecklistIdRef.current = session.suspendFillChecklistId;
      }

      // Soft-close avvik/modul-detalj while placing pin (mirror checklist soft-close).
      const detailModule =
        viewer.moduleDetailModule ||
        (session.suspendModuleDetailId != null
          ? viewer.modules.find(
              (m) => String(m.id) === String(session.suspendModuleDetailId)
            )
          : null);
      if (detailModule || session.suspendModuleDetailId != null) {
        suspendedModuleDetailRef.current =
          detailModule ||
          suspendedModuleDetailRef.current || {
            id: session.suspendModuleDetailId,
            deviation_id: session.suspendDeviationId,
            module_type: "circle",
            path_data: JSON.stringify({ kind: "deviation" }),
            deviation:
              session.suspendDeviationId != null
                ? { id: session.suspendDeviationId }
                : null,
          };
        updateDocumentPlacementSession({
          suspendModuleDetailId:
            suspendedModuleDetailRef.current?.id ??
            session.suspendModuleDetailId,
          suspendDeviationId:
            session.suspendDeviationId ??
            suspendedModuleDetailRef.current?.deviation_id ??
            null,
        });
        viewer.setModuleDetailModule(null);
      }

      closeMobileSheet();
      clearPinFocusMode();
      viewer.startDocumentPinPlacementFromSession(session);
    };

    return registerDrawingViewerPlacementBridge({
      drawingId: viewer.drawing.id,
      startPlacement,
    });
  }, [
    viewer.drawing?.id,
    viewer.startDocumentPinPlacementFromSession,
    viewer.moduleDetailModule,
    viewer.modules,
    viewer.setModuleDetailModule,
    fillChecklistId,
    closeMobileSheet,
    softCloseFillDialog,
    clearPinFocusMode,
  ]);

  // After document pin place/cancel, reopen suspended checklist fill / deviation detail.
  useEffect(() => {
    return subscribeDocumentPlacement((session) => {
      if (!session) return;
      if (session.status !== "completed" && session.status !== "cancelled") {
        return;
      }
      const resumeId =
        session.suspendFillChecklistId || suspendedFillChecklistIdRef.current;
      if (resumeId) {
        suspendedFillChecklistIdRef.current = null;
        // Soft refresh after place so new pin/docs appear; cache avoids spinner.
        openFillChecklist(resumeId, {
          resumeMode:
            session.status === "completed" ? "cache-soft-refresh" : "cache",
        });
      }

      const suspendedDetail = suspendedModuleDetailRef.current;
      const resumeDetailId =
        session.suspendModuleDetailId || suspendedDetail?.id || null;
      if (resumeDetailId != null || suspendedDetail) {
        const liveModule =
          (resumeDetailId != null &&
            viewer.modules.find(
              (m) => String(m.id) === String(resumeDetailId)
            )) ||
          suspendedDetail;
        suspendedModuleDetailRef.current = null;
        if (liveModule) {
          viewer.setModuleDetailModule(liveModule);
        }
      }
    });
  }, [openFillChecklist, viewer.modules, viewer.setModuleDetailModule]);

  // Checklist pin / panel → open fill-out directly.
  useEffect(() => {
    const onOpenFill = (event) => {
      const checklistId = event?.detail?.checklistId;
      if (checklistId == null) return;
      const sameCached =
        fillChecklistId != null &&
        String(fillChecklistId) === String(checklistId);
      openFillChecklist(checklistId, {
        resumeMode: sameCached ? "cache" : "network",
      });
    };
    window.addEventListener("promang:open-checklist-fill", onOpenFill);
    return () => window.removeEventListener("promang:open-checklist-fill", onOpenFill);
  }, [openFillChecklist, fillChecklistId]);

  // Leave pin-focus when starting placement or opening add dialog.
  useEffect(() => {
    if (viewer.pendingObservation || viewer.addDialogOpen) {
      clearPinFocusMode();
    }
  }, [viewer.pendingObservation, viewer.addDialogOpen, clearPinFocusMode]);

  // Leave pin-focus when the selected pin is cleared or replaced by an unrelated module.
  useEffect(() => {
    if (pinFocusChecklistId == null && pinFocusDetailModule == null) return;
    const selected = viewer.selectedModule;
    if (!selected) {
      clearPinFocusMode();
      return;
    }
    if (pinFocusChecklistId != null) {
      const selectedChecklistId = checklistIdFromModule(selected);
      if (
        selectedChecklistId != null &&
        String(selectedChecklistId) !== String(pinFocusChecklistId)
      ) {
        clearPinFocusMode();
      }
    }
  }, [
    viewer.selectedModule,
    pinFocusChecklistId,
    pinFocusDetailModule,
    clearPinFocusMode,
  ]);

  // If we landed here via navigation with an active session, ensure fill is soft-closed.
  useEffect(() => {
    const session = getDocumentPlacementSession();
    if (session?.status === "placing" && (fillChecklistId || fillDialogOpen)) {
      const activeFillId =
        fillChecklistId || session.suspendFillChecklistId || null;
      if (activeFillId) {
        suspendedFillChecklistIdRef.current = activeFillId;
        updateDocumentPlacementSession({
          suspendFillChecklistId: activeFillId,
        });
        if (fillChecklistId == null) setFillChecklistId(activeFillId);
      }
      softCloseFillDialog();
      setFillResumeMode("cache-soft-refresh");
    }
  }, [fillChecklistId, fillDialogOpen, viewer.drawing?.id, softCloseFillDialog]);

  const handleOpenPanelTab = useCallback(
    (tab) => {
      viewer.setPanelTab(tab);
      if (isMobile) {
        setMobileSheet("panel");
      } else {
        viewer.setPanelOpen(true);
      }
    },
    [isMobile, viewer]
  );

  const handleToggleSelectPan = useCallback(() => {
    setMobileSheet(null);
    viewer.setTool(viewer.tool === "pan" ? "select" : "pan");
  }, [viewer]);

  const handleMobileModuleCenter = useCallback(
    (module) => {
      viewer.handleCenterModule(module);
    },
    [viewer]
  );

  if (viewer.loading) {
    return <ResourceLoading />;
  }

  if (!viewer.drawing) {
    return <div className="p-6">{viewer.t("drawings.load_error")}</div>;
  }

  const panelProps = {
    t: viewer.t,
    canEdit: viewer.canEdit,
    drawing: viewer.drawing,
    modules: viewer.modules,
    selectedModule: viewer.selectedModule,
    pendingChanges: viewer.pendingChanges,
    panelOpen: viewer.panelOpen,
    tab: viewer.panelTab,
    onTabChange: viewer.setPanelTab,
    onSelectModule: viewer.setSelectedModule,
    onDeleteModule: viewer.handleDeleteModule,
    onCenterModule: (module) => {
      viewer.handleCenterModule(module);
      if (isMobile && mobileSheet === "panel") {
        setMobileSheet(null);
      }
    },
    deviations: viewer.deviations,
    checklists: viewer.checklists,
    relatedLoading: viewer.relatedLoading,
    measureScale: viewer.measureScale,
    onPinDeviation: (deviationId) => {
      viewer.handlePinDeviation(deviationId);
      if (isMobile) {
        setMobileSheet(null);
      }
    },
    onStartCalibration: viewer.startCalibrationLine,
    onCreateVersion: viewer.handleCreateVersion,
    onSwitchVersion: viewer.switchVersion,
    activeVersionId: viewer.activeVersionId,
    versions: viewer.versions,
    creatingVersion: viewer.creatingVersion,
    expandedChecklistId: viewer.expandedChecklistId,
    onToggleChecklist: (checklistId) =>
      viewer.setExpandedChecklistId((current) =>
        current === checklistId ? null : checklistId
      ),
    draftDrawingFloorId: viewer.draftDrawingFloorId,
    setDraftDrawingFloorId: viewer.setDraftDrawingFloorId,
    locationDirty: viewer.locationDirty,
    savingLocation: viewer.savingLocation,
    onSaveLocation: viewer.handleSaveLocation,
    onOpenModuleDetail: viewer.handleOpenModuleDetail,
    onAddChecklist: () => {
      viewer.openAddChecklist();
      if (isMobile) setMobileSheet(null);
    },
    onLinkChecklist: (checklistId) => {
      viewer.openLinkChecklist(checklistId);
      if (isMobile) setMobileSheet(null);
    },
    onFillChecklist: (checklistId) => {
      const sameCached =
        fillChecklistId != null &&
        String(fillChecklistId) === String(checklistId);
      openFillChecklist(checklistId, {
        resumeMode: sameCached ? "cache" : "network",
      });
    },
  };

  return (
    <div
      ref={viewerShellRef}
      className={`grid grid-rows-[auto_1fr] bg-white ${IMMERSIVE_VIEWER_SHELL_CLASS}`}
    >
      <DrawingViewerTopBar
        t={viewer.t}
        drawing={viewer.drawing}
        page={viewer.page}
        pageCount={viewer.numPages}
        onPageChange={viewer.handlePageChange}
        zoom={viewer.zoom}
        onZoomChange={viewer.setZoomCentered}
        onFit={viewer.fit}
        pendingCount={viewer.pendingCount}
        saving={viewer.saving}
        onSaveChanges={viewer.handleSaveAllChanges}
        onEdit={viewer.goToEdit}
        onTogglePanel={() => viewer.setPanelOpen((open) => !open)}
        isVersionMode={viewer.isVersionMode}
        activeVersion={viewer.activeVersion}
        versions={viewer.versions}
        onCreateVersion={viewer.handleCreateVersion}
        onSwitchVersion={viewer.switchVersion}
        creatingVersion={viewer.creatingVersion}
        onAdd={() => viewer.openAddDialog("photo")}
        placingObservation={viewer.placingObservation}
        siblingDrawings={viewer.siblingDrawings}
        siblingDrawingsLoading={viewer.siblingDrawingsLoading}
        canEdit={viewer.canEdit}
        onOpen3d={viewer.ifcEnabled ? viewer.handleOpen3d : undefined}
        onAlign3d={viewer.ifcCanEdit ? viewer.startIfcAlignment : undefined}
        aligning3d={viewer.ifcAlignmentActive || viewer.ifcAlignmentDialogOpen}
      />

      <DrawingViewerMobileTopBanner
        t={viewer.t}
        drawing={viewer.drawing}
        isVersionMode={viewer.isVersionMode}
        activeVersion={viewer.activeVersion}
        versions={viewer.versions}
        onOpenDrawingPicker={() => openMobileSheet("drawings")}
      />

      <div
        className={`relative grid min-h-0 min-w-0 w-full max-w-full overflow-x-hidden ${
          viewer.panelOpen
            ? "xl:grid-cols-[56px_1fr_328px]"
            : "xl:grid-cols-[56px_1fr]"
        } grid-cols-1 pb-[calc(var(--mobile-nav-height,3.75rem)+env(safe-area-inset-bottom,0px))] xl:pb-0`}
      >
        <DrawingViewerToolRail
          t={viewer.t}
          tool={viewer.tool}
          onToolChange={viewer.setTool}
          canEdit={viewer.canEdit}
        />

        <div className="relative flex h-full min-h-0 min-w-0 max-w-full flex-col overflow-x-hidden">
          {canShowDrawing3dFab({
            companyHasIfcModule: viewer.ifcModuleActive,
            userHasIfcRole: viewer.ifcRoleAccess,
            projectId: viewer.drawing.project?.id,
          }) ? (
            <DrawingViewer3dFab
              t={viewer.t}
              onClick={viewer.handleOpen3d}
            />
          ) : null}
          <DrawingViewerCanvas
            t={viewer.t}
            containerRef={viewer.containerRef}
            sheetRef={viewer.sheetRef}
            pdfUrl={viewer.pdfUrl}
            page={viewer.page}
            numPages={viewer.numPages}
            zoom={viewer.zoom}
            pan={viewer.pan}
            pdfBaseSize={viewer.pdfBaseSize}
            pdfError={viewer.pdfError}
            drawing={viewer.drawing}
            modules={viewer.visibleModules}
            pendingChanges={viewer.pendingChanges}
            selectedModule={viewer.selectedModule}
            draftRect={viewer.draftRect}
            draftPen={viewer.draftPen}
            draftMeasure={viewer.draftMeasure}
            strokeWidth={viewer.strokeWidth}
            tool={viewer.tool}
            markupColor={viewer.markupColor}
            isShapeTool={viewer.isShapeTool}
            workspaceClass={viewer.workspaceClass}
            measureScale={viewer.measureScale}
            isVersionMode={viewer.isVersionMode}
            activeVersion={viewer.activeVersion}
            onPointerDown={viewer.handlePointerDown}
            onPointerMove={viewer.handlePointerMove}
            onPointerUp={viewer.handlePointerUp}
            onSelectModule={viewer.setSelectedModule}
            onModuleMove={viewer.handleModuleMove}
            onModuleConfigure={viewer.handleModuleConfig}
            onModuleDelete={viewer.handleDeleteModule}
            onOpenModuleDetail={viewer.handleOpenModuleDetail}
            onFocusOnDrawing={handleFocusModuleOnDrawing}
            onPdfLoadSuccess={viewer.handlePdfLoadSuccess}
            onPdfLoadError={viewer.handlePdfLoadError}
            onPageLoadSuccess={viewer.handlePageLoadSuccess}
            onZoomChange={viewer.setZoomCentered}
            onFit={viewer.fit}
            onPageChange={viewer.handlePageChange}
            onMarkupColorChange={viewer.setMarkupColor}
            pendingObservation={viewer.pendingObservation}
            placementGhost={viewer.placementGhost}
            onCancelPlacement={viewer.cancelObservationPlacement}
            onWantSketchChange={viewer.setDocumentPinWantSketch}
            relocatingModuleId={viewer.relocatingModuleId}
            isModuleDraggable={viewer.isModuleDraggable}
            onStartRelocate={viewer.startRelocateModule}
            onRetrySync={viewer.retryFailedPin}
            checklists={viewer.checklists}
            ifcContextMenu={viewer.ifcContextMenu}
            onContextMenu={viewer.handleCanvasContextMenu}
            onOpenPlaceIn3d={viewer.handleOpenPlaceIn3d}
            onCloseContextMenu={viewer.closeIfcContextMenu}
            ifcAlignmentActive={viewer.ifcAlignmentActive}
            ifcAlignmentPoints={viewer.ifcAlignmentPoints}
            onCancelIfcAlignment={viewer.cancelIfcAlignment}
          />

          {(pinFocusChecklistId != null || pinFocusDetailModule != null) && (
            <div
              className={`pointer-events-none absolute inset-x-0 z-[35] flex justify-center px-4 ${
                viewer.isShapeTool ? "top-[4.25rem]" : "top-3.5"
              }`}
            >
              <div className="pointer-events-auto flex max-w-[min(100%,22rem)] items-center gap-1 rounded-full border border-[#bfdcf7] bg-white/95 p-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                <button
                  type="button"
                  onClick={
                    pinFocusChecklistId != null
                      ? handleReopenChecklistFromPinFocus
                      : handleReopenDetailFromPinFocus
                  }
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-[#2b6ef0] px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#1d4fb0]"
                >
                  {pinFocusChecklistId != null ? (
                    <ClipboardDocumentCheckIcon className="h-4 w-4 flex-none" />
                  ) : (
                    <ExclamationTriangleIcon className="h-4 w-4 flex-none" />
                  )}
                  <span className="truncate">
                    {pinFocusChecklistId != null
                      ? viewer.t("drawings.viewer.reopen_checklist")
                      : viewer.t("drawings.viewer.reopen_deviation")}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={clearPinFocusMode}
                  className="grid h-11 w-11 flex-none place-items-center rounded-full text-[#59616d] hover:bg-[#eef0f3]"
                  title={viewer.t("drawings.viewer.dismiss_pin_focus")}
                  aria-label={viewer.t("drawings.viewer.dismiss_pin_focus")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {viewer.panelOpen && !isMobile && <DrawingViewerPanel {...panelProps} />}
      </div>

      <DrawingViewerMobileBottomBar
        t={viewer.t}
        tool={viewer.tool}
        onToggleSelectPan={handleToggleSelectPan}
        onOpenTools={() => openMobileSheet("tools")}
        onAdd={() => {
          closeMobileSheet();
          viewer.openAddDialog("photo");
        }}
        onOpenModules={() => {
          if (mobileSheet === "modules") {
            closeMobileSheet();
          } else {
            setMobileSheet("modules");
          }
        }}
        onOpenMore={() => openMobileSheet("more")}
        placingObservation={viewer.placingObservation}
        modulesOpen={mobileSheet === "modules"}
        toolsOpen={mobileSheet === "tools"}
        moreOpen={mobileSheet === "more" || mobileSheet === "panel"}
        canEdit={viewer.canEdit}
      />

      <DrawingViewerMobileModulesSheet
        t={viewer.t}
        open={mobileSheet === "modules"}
        onClose={closeMobileSheet}
        modules={viewer.modules}
        selectedModule={viewer.selectedModule}
        pendingChanges={viewer.pendingChanges}
        measureScale={viewer.measureScale}
        onSelectModule={viewer.setSelectedModule}
        onDeleteModule={viewer.handleDeleteModule}
        onCenterModule={handleMobileModuleCenter}
        onOpenModuleDetail={viewer.handleOpenModuleDetail}
        scrollTopRef={modulesScrollTopRef}
        canEdit={viewer.canEdit}
      />

      <DrawingViewerMobileToolsSheet
        t={viewer.t}
        open={mobileSheet === "tools"}
        onClose={closeMobileSheet}
        tool={viewer.tool}
        onToolChange={viewer.setTool}
        zoom={viewer.zoom}
        onZoomChange={viewer.setZoomCentered}
        onFit={viewer.fit}
        canEdit={viewer.canEdit}
      />

      <DrawingViewerMobileDrawingPicker
        t={viewer.t}
        open={mobileSheet === "drawings"}
        onClose={closeMobileSheet}
        drawings={viewer.siblingDrawings}
        currentId={viewer.drawing.id}
        loading={viewer.siblingDrawingsLoading}
        hasProject={!!viewer.drawing.project?.id}
      />

      <DrawingViewerMobileMoreSheet
        t={viewer.t}
        open={mobileSheet === "more"}
        onClose={closeMobileSheet}
        pendingCount={viewer.pendingCount}
        saving={viewer.saving}
        onSaveChanges={viewer.handleSaveAllChanges}
        onEdit={viewer.goToEdit}
        onOpenPanelTab={handleOpenPanelTab}
        onStartAddDeviation={() => viewer.openAddDialog("deviation")}
        isVersionMode={viewer.isVersionMode}
        onSwitchVersion={viewer.switchVersion}
        onCreateVersion={viewer.handleCreateVersion}
        creatingVersion={viewer.creatingVersion}
        onStartCalibration={viewer.startCalibrationLine}
        canEdit={viewer.canEdit}
        onOpen3d={viewer.ifcEnabled ? viewer.handleOpen3d : undefined}
        onAlign3d={viewer.ifcCanEdit ? viewer.startIfcAlignment : undefined}
        aligning3d={viewer.ifcAlignmentActive || viewer.ifcAlignmentDialogOpen}
      />

      {mobileSheet === "panel" && (
        <div className="fixed inset-0 z-[60] xl:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 border-none bg-[rgba(15,23,42,0.42)]"
            aria-label={viewer.t("drawings.viewer.mobile.close")}
            onClick={closeMobileSheet}
          />
          <div className="absolute inset-x-0 bottom-0 top-[10%] flex flex-col overflow-hidden rounded-t-[18px] bg-white shadow-[0_-12px_40px_rgba(15,23,42,0.18)]">
            <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-[#e5e8ec] px-3.5 py-2.5">
              <h2 className="text-[15px] font-bold text-[#14171c]">
                {viewer.t("drawings.viewer.mobile.more")}
              </h2>
              <button
                type="button"
                onClick={closeMobileSheet}
                className="grid h-11 w-11 place-items-center rounded-[10px] text-[#59616d] hover:bg-[#eef0f3]"
                aria-label={viewer.t("drawings.viewer.mobile.close")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <DrawingViewerPanel
              {...panelProps}
              embedded
              hideModulesTab
              panelOpen
            />
          </div>
        </div>
      )}

      <DrawingModuleConfigDialog
        t={viewer.t}
        moduleConfigDialog={viewer.moduleConfigDialog}
        saving={viewer.saving}
        onClose={() => viewer.setModuleConfigDialog(null)}
        onChange={viewer.setModuleConfigDialog}
        onSave={viewer.handleSaveModuleConfig}
      />

      <DrawingCalibrationDialog
        t={viewer.t}
        open={viewer.calibrateOpen}
        referenceLine={viewer.calibrateLine}
        scale={viewer.measureScale}
        onClose={() => {
          viewer.setCalibrateOpen(false);
          viewer.setCalibrateLine(null);
        }}
        onApply={viewer.handleSaveMeasureScale}
        saving={viewer.saving}
      />

      <DrawingAddObservationDialog
        t={viewer.t}
        open={viewer.addDialogOpen}
        onClose={() => viewer.setAddDialogOpen(false)}
        onPlace={viewer.startObservationPlacement}
        page={viewer.page}
        drawingTitle={viewer.drawing.title}
        drawing={viewer.drawing}
        checklists={viewer.checklists}
        projectId={viewer.drawing.project?.id}
        modules={viewer.modules}
        initialKind={viewer.addDialogKind}
        initialChecklistId={viewer.addDialogChecklistId}
        initialChecklistMode={viewer.addDialogChecklistMode}
      />

      <DrawingObservationSketchOverlay
        t={viewer.t}
        open={!!viewer.sketchSession}
        backgroundUrl={viewer.sketchSession?.previewUrl}
        onComplete={viewer.finishSketchSession}
        onSkip={viewer.skipSketchSession}
        onCancel={viewer.cancelSketchSession}
      />

      <DrawingModuleDetailDialog
        t={viewer.t}
        open={!!viewer.moduleDetailModule}
        module={viewer.moduleDetailModule}
        drawing={viewer.drawing}
        checklists={viewer.checklists}
        onClose={() => viewer.setModuleDetailModule(null)}
        onToggleSnapshotPrint={viewer.handleToggleSnapshotPrint}
        onFillChecklist={(checklistId) => {
          viewer.setModuleDetailModule(null);
          const sameCached =
            fillChecklistId != null &&
            String(fillChecklistId) === String(checklistId);
          openFillChecklist(checklistId, {
            resumeMode: sameCached ? "cache" : "network",
          });
        }}
        onAttachPhoto={(checklistId) => {
          viewer.setModuleDetailModule(null);
          viewer.openAddDialog({ kind: "photo", checklistId });
        }}
        onAttachExistingPhoto={viewer.attachExistingPhotoToChecklist}
        modules={viewer.modules}
        onOpenDeviation={handleOpenDeviationFromPhoto}
        onShowOnDrawing={(module) => {
          enterPinFocusMode(module);
        }}
        onFocusDocumentPin={handleFocusDocumentPin}
        focusedDocumentId={
          focusedDocumentId ||
          documentIdFromModule(viewer.selectedModule) ||
          null
        }
      />

      <DrawingChecklistFillDialog
        t={viewer.t}
        open={fillDialogOpen && !!fillChecklistId}
        checklistId={fillChecklistId}
        resumeMode={fillResumeMode}
        onClose={hardCloseFillDialog}
        onSessionSnapshot={handleFillSessionSnapshot}
        onFocusDocumentPin={handleFocusDocumentPin}
        focusedDocumentId={
          focusedDocumentId ||
          documentIdFromModule(viewer.selectedModule) ||
          null
        }
        onChecklistUpdated={() => {
          if (viewer.drawing?.project?.id) {
            viewer.loadRelatedData?.(viewer.drawing.project.id);
          }
        }}
      />

      <DrawingIfcAlignmentDialog
        t={viewer.t}
        open={viewer.ifcAlignmentDialogOpen}
        points={viewer.ifcAlignmentPoints}
        saving={viewer.savingIfcAlignment}
        onClose={viewer.cancelIfcAlignment}
        onSave={viewer.handleSaveIfcAlignment}
      />
    </div>
  );
};
